<template>
  <DocLayout v-if="!isLoading" :doc="doc" :show-text-controls="false">
    <template #page-title>
      <h1 class="title-section__title">Create a new discussion post</h1>
    </template>
    <template #page-content>
      <div class="book-reading__content-editor">
        <form
          class="form thread-edit"
          role="form"
          novalidate
          @submit.prevent="save"
        >
          <fieldset v-if="!hasUsername" class="thread-edit__row">
            <label class="form__label form__label--large">Username</label><br />
            <UsernameInput :model="model" :validation="validation" />
          </fieldset>
          <fieldset class="thread-edit__row">
            <label class="form__label form__label--large">Title</label><br />
            <label class="form__label text-muted"
              >To get more help, write a descriptive title (like what type of
              situation you're dealing with).</label
            >
            <InputWithContextMenu
              id="tread-edit-title"
              v-model="model.title"
              class="form-control"
              placeholder="Enter title here"
              type="text"
              :class="validation.stateClass('title')"
            />
            <FormError :errors="validation.errors" field="title" />
          </fieldset>
          <fieldset class="thread-edit__row">
            <label class="form__label form__label--large">Body</label><br />
            <!--
              WYSIWYG editor for forum is not implemented yet.

              div
                  data-toggle="quill"
                  data-quill-placeholder="Quill WYSIWYG"
                ></div-->
            <InputWithContextMenu
              id="tread-edit-text"
              ref="inputRef"
              v-model="model.text"
              elementType="textarea"
              class="form-control"
              placeholder="Enter post body here"
              rows="8"
              type="text"
              :class="validation.stateClass('text')"
            />
            <FormError :errors="validation.errors" field="text" />
          </fieldset>
          <div
            v-if="validation.errors && validation.errors._schema"
            class="alert alert-danger"
          >
            {validation.errors._schema}
          </div>
          <button v-wait:disabled="'Posting'" type="submit" class="btns">
            <v-wait for="Posting">
              <template v-slot:waiting>
                Posting
                <button-spinner />
              </template>
              Post for Discussion
            </v-wait>
          </button>
        </form>
      </div>
    </template>
  </DocLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRouter } from 'vue-router'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'

import DocLayout from '@/app/components/doc/Layout.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'

import UsernameInput from './UsernameInput.vue'

import { ValidationState } from '@/helpers/form'
import { auth } from '@/services/auth'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { ThreadView } from '@/models/forum'
import { setInputFocusStart } from '@/helpers/form'
import { DocView } from '@/models/doc'
import { Thread } from '@/models/interfaces'
import { assertNotNull } from '@/helpers/typing'

const EMPTY_THREAD: Partial<Thread> = { title: '', text: '' }

const props = defineProps<{
  doc: DocView
}>()

const router = useRouter()
const wait = useWait()

const inputRef = ref<InstanceType<typeof InputWithContextMenu> | null>(null)

const threadUrlSlug = ref<string | null>(null)

const isLoading = ref(false)
const validation = ref(new ValidationState())
const model = ref({ ...EMPTY_THREAD })

onMounted(() => {
  // Retrieve the threadUrlSlug from the history api state
  threadUrlSlug.value = window.history.state.threadUrlSlug
  const threadTitle = window.history.state.threadTitle
  const threadText = window.history.state.threadText
  const userWorkoutId = window.history.state.userWorkoutId

  if (!threadUrlSlug.value) {
    // The component was pre-filled with the exercise content,
    // put the cursor at the start of the text
    setInputFocusStart(inputRef.value?.$el)
  } else {
    loadThread()
  }
  if (threadTitle) {
    model.value.title = threadTitle
  }
  if (threadText) {
    model.value.text = threadText
  }
  if (userWorkoutId) {
    model.value.user_workout_id = userWorkoutId
  }
})

const hasUsername = computed(() => {
  return !!auth.getUsername()
})

async function save(): Promise<void> {
  try {
    if (!model.value) {
      throw new Error('Can not save thread, no model')
    }
    wait.start('Posting')
    const thread = await backend.saveThread(props.doc, model.value)
    validation.value.reset()
    router.push({
      name: props.doc.routeNames().thread,
      params: { thread: thread.url_slug },
    })
  } catch (error) {
    validation.value.showErrors(error)
  } finally {
    wait.end('Posting')
  }
}

async function loadThread(): Promise<void> {
  try {
    isLoading.value = true
    const response = await backend.getThread(assertNotNull(threadUrlSlug.value))
    model.value = new ThreadView(response)
    isLoading.value = false
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else {
      Sentry.captureException(error)
      throw error
    }
  }
}
</script>
